import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import { deleteUserById, getUsersWithPagination } from '@app/services/user';
import { confirmAlert } from 'react-confirm-alert';
import { toast } from 'react-toastify';
import DataTable, { TableStyles } from 'react-data-table-component';
import { Button } from '@app/components';
import { getAllRolesExceptEU } from '@app/services/role';
import { useSelector } from 'react-redux';
import ExcelJS from "exceljs/dist/es5/exceljs.browser";
import moment from 'moment';
import fileDownload from 'js-file-download';
import Select from "react-select";
import { getAllActiveFranchise } from '@app/services/franchise';
import { getCentresByFranchiseId } from '@app/services/centre';
import { encodeId } from '@app/helpers/base64url';
import AssignMentorCentresModal from './AssignMentorCentresModal';

const ListUsers = () => {

    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('');
    const [search, setSearch] = useState({firstName: '', lastName: '', email: '', phone: '', role: '', status: '', franchiseId: '', centreId: ''});
    const userStatus = [{label:'Active', value:'active'}, {label:'Inactive', value:'inactive'}]
    const permissions = useSelector((state: any) => state.auth.permissions);
    const [franchises, setFranchises] = useState<any[]>([]);
    const [centres, setCentres] = useState<any[]>([]);
    const [showMentorModal, setShowMentorModal] = useState(false);
    const [mentorUser, setMentorUser] = useState<any>(null);

    const customStyle: TableStyles = {
        head: {
            style: {
                color: "#5E5873",
                fontSize: "13px",
                fontWeight: 900,
                // borderBottomWidth: '0px',
                // borderBottomColor: theme.divider.default,
                // borderBottomStyle: 'solid',
            },
        },
        headCells: {
            style: {
                borderColor: "#E9ECEF",
                textTransform: "uppercase",
                letterSpacing: "1px",
            },
        },
        headRow: {
            style: {
                backgroundColor: "#F7F7F8",
                minHeight: "44px",
                fontWeight: 500,
            },
        },
    };
      
    useEffect(() => {
        getAllUsersData(1, perPage, sortField, sortDirection);
    },[search]);

    useEffect(() => {
        getAllRolesData();
        fetchFranchises();
    },[]);

    const fetchFranchises = async () => {
        try {
            const resp = await getAllActiveFranchise();
            if (resp?.success === true) setFranchises(resp.collections || []);
        } catch (error: any) {
            toast.error(error?.message || 'Internal Server Error');
        }
    };

    const fetchCentres = async (franchiseId: any) => {
        try {
            const resp = await getCentresByFranchiseId(franchiseId);
            if (resp?.success === true) setCentres(resp.collections || []);
        } catch (error: any) {
            toast.error(error?.message || 'Internal Server Error');
        }
    };

    const getAllRolesData = async () => {
        try {
            const roleGroupsResp = await getAllRolesExceptEU();
            if (roleGroupsResp.success === true) {
                setRoles(roleGroupsResp.collections);
            } else {
                toast.error(roleGroupsResp?.errors[0]?.msg);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const getAllUsersData = async (page, perPage, sortField, sortDirection) => {
        try {
            setLoading(true);
            const usersResp = await getUsersWithPagination(page, perPage, sortField, sortDirection, search);
            if (usersResp.success === true) {
                setLoading(false);
                setUsers(usersResp.collections.data);
                setTotalRows(usersResp?.collections?.pagination?.total);
            } else {
                setLoading(false);
                toast.error(usersResp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const columns = [
        // {
        //     name: '#ID',
        //     selector: (row: { id: number; }) => row.id,
        //     sortable: true,
        // },
        {
            name: 'First Name',
            selector: (row: { first_name: string; }) => row.first_name,
            sortable: true,
            sortField: 'first_name',
        },
        {
            name: 'Last Name',
            selector: (row: { last_name: string; }) => row.last_name,
            sortable: true,
            sortField: 'last_name',
        },
        {
            name: 'Email',
            selector: (row: { email: string; }) => row.email,
            sortable: true,
            sortField: 'email',
            wrap: true,
        },
        {
            name: 'Phone',
            selector: (row: { phone: string; }) => row.phone ? row.phone : '---',
            sortable: true,
            sortField: 'phone',
        },
        // {
        //     name: 'Address',
        //     selector: (row: { address: string; }) => row.address,
        //     sortable: true,
        //     sortField: 'address',
        // },
        {
            name: 'Role(s)',
            selector: (row: { roles: string; }) => { return Array.prototype.map.call(row.roles, function(item) { return item.name; }).join(", "); },
            sortable: true,
            sortField: 'role',
            wrap: true,
        },
        {
            name: <div><div>Franchises/</div><div>Centres</div></div>,
            cell: (row: any) => {
                // Merge the per-role franchise/centre counts into one row per role
                const franchiseByRole = row.total_franchise_by_role || [];
                const centreByRole = row.total_centre_by_role || [];
                const roleIds = Array.from(new Set([
                    ...franchiseByRole.map((r: any) => r.role_id),
                    ...centreByRole.map((r: any) => r.role_id),
                ]));

                // Franchise Attached only applies to the Franchise Owner role;
                // every other FC-A role attaches to centres instead. FMS-A roles
                // don't appear in these by-role arrays at all, so they're skipped
                // here without any extra filtering.
                const franchiseRows = franchiseByRole.filter((r: any) => r.slug === 'franchise-owner');
                const centreRows = centreByRole.filter((r: any) => r.slug !== 'franchise-owner' && r.total_centres > 0);

                if (roleIds.length === 0 || (franchiseRows.length === 0 && centreRows.length === 0)) {
                    return <span>---</span>;
                }

                return (
                    <div>
                        {centreRows.length > 0 && (
                            <div className="mb-1">
                                <div className="font-weight-bold" style={{ fontSize: '12px' }}>Centres</div>
                                {centreRows.map((r: any) => (
                                    <div key={r.role_id}><small className="text-muted">{r.role_name}: {r.total_centres}</small></div>
                                ))}
                            </div>
                        )}
                        {franchiseRows.length > 0 && (
                            <div>
                                <div className="font-weight-bold" style={{ fontSize: '12px' }}>Franchise</div>
                                {franchiseRows.map((r: any) => (
                                    <div key={r.role_id}><small className="text-muted">{r.role_name}: {r.total_franchises}</small></div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            },
            sortable: false,
            width: '160px',
        },
        {
            name: 'Status',
            selector: (row: { status: string; }) => row.status,
            cell: (row: { status: string; }) => (
                row.status === 'active' ? <span className="badge bg-success">Active</span> : <span className="badge bg-danger">Inactive</span>
            ),
            sortable: true,
            sortField: 'status',
        },
        {  
            name: 'Action',
            cell: (row: any) => (
              <>
                { permissions?.users?.edit && 
                    <Link
                    to={`/users/edit/${encodeId(row.id)}`}
                    role="button"
                    state={{ id: row.id }}
                    >
                    <i className="fas fa-pen mr-2"/>
                    </Link>
                }
                { permissions?.users?.view && 
                    <Link
                    to={`/users/view/${encodeId(row.id)}`}
                    role="button"
                    state={{ id: row.id }}
                    data-toggle="tooltip" title="View"
                    >
                    <i className="fas fa-eye mr-2"/>
                    </Link>
                }
                { permissions?.users['update-dbs'] && 
                    <Link
                        to={`/users/dbs`}
                        role="button"
                        state={{ id: row.id, userName: `${row.first_name} ${row.last_name}` }}
                        data-toggle="tooltip" title="Update DBS Cert Details"
                    >
                        <i className="fas fa-file-signature mr-2"/>
                    </Link>
                }
                { permissions?.users?.delete &&
                    <a href='#' onClick={ (e) => { deleteUserConfirm(e, row.id)} }>
                        <i className="fas fa-trash mr-2"/>
                    </a>
                }
                { permissions?.users?.edit && row.roles?.some((r: any) => r.slug === 'mentor') &&
                    <a href='#' onClick={ (e) => { e.preventDefault(); setMentorUser(row); setShowMentorModal(true); } } data-toggle="tooltip" title="Assign Centers (Mentor role)">
                        <i className="fas fa-building mr-2"/>
                    </a>
                }
              </>
            ),
        },
    ];

    const deleteUserConfirm = (e, id) => {
        confirmAlert({
            title: 'Confirm to submit',
            message: 'Are you sure to delete this?',
            buttons: [
                {
                    label: 'Yes',
                    onClick: () => deleteUser(id)
                },
                {
                    label: 'No',
                    onClick: () => {}
                }
            ]
        });
        e.preventDefault();
    };

    const deleteUser = async (id) => {
        try {
            setLoading(true);
            const resp = await deleteUserById(id);
            if(resp.success === true){
                toast.success(resp?.message);
                setLoading(false);
                getAllUsersData(currentPage, perPage, sortField, sortDirection);
            }else{
                toast.error(resp?.errors[0]?.msg);
                setLoading(false);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const handlePageChange = page => {
        getAllUsersData(page, perPage, sortField, sortDirection);
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        getAllUsersData(page, newPerPage, sortField, sortDirection);
        setPerPage(newPerPage);
    };

    const handleSort = async (column, sortDirection) => {
        setSortField(column.sortField);
        setSortDirection(sortDirection);
        getAllUsersData(currentPage, perPage, column.sortField, sortDirection);
    };

    const handleResetFilter = async () => {
        setSearch({firstName: '', lastName: '', email: '', phone: '', role: '', status: '', franchiseId: '', centreId: ''});
        setCentres([]);
    };

    const exportCSV = async() => {
        try {
            setLoading(true);
            let records = await getUsersWithPagination(1, totalRows, sortField, sortDirection, search)
            if (records.success === true) {
                setLoading(false);
            } else {
                setLoading(false);
                toast.error(records?.errors[0]?.msg);
                return;
            }

            if((records?.collections?.data).length === 0){
                toast.error('Records not found.')
                return;
            }

            const workbook = new ExcelJS.Workbook();
            workbook.addWorksheet("Users");
            const worksheet = workbook.getWorksheet('Users');
            worksheet.columns = [
                { header: "First Name", key: "first_name" },
                { header: "Last Name", key: "last_name" },
                { header: "Email", key: "email" },
                { header: "Alternate Email", key: "alt_email" },
                { header: "Mobile", key: "phone" },
                { header: "Alternate Mobile", key: "alt_phone" },
                { header: "Address", key: "address" },
                { header: "Role(s)", key: "roles" },
                { header: "Franchise Attached", key: "franchise" },
                { header: "Total Franchise", key: "total_franchise" },
                { header: "Centre  Attached", key: "centre" },
                { header: "Total Centre", key: "total_centre" },
                { header: "Status", key: "status" },
                { header: "Last Login At", key: "last_login" }
            ];
            
            (records?.collections?.data).map((row) => {
                row.roles = row.roles !== null ? ( Array.prototype.map.call(row.roles, function(role) { return role.name; }).join(", ") ) : '---';
                row.franchise = row.franchise !== null ? row.franchise : '---';
                row.centre = row.centre !== null ? row.centre : '---';
                row.alt_email = (row.alt_email != null) ? row.alt_email : '---';
                row.alt_phone = (row.alt_phone != null) ? row.alt_phone : '---';
                row.status = row.status.charAt(0).toUpperCase() + row.status.slice(1);
                row.last_login = (row.last_login != null) ? moment(row.last_login).format('DD/MM/YYYY hh:mm A') : '---';
            })

            worksheet.addRows(records?.collections?.data);
            let filename = 'Users-' + moment().format('DD-MMM-YYYY') + '.csv';

            workbook.csv.writeBuffer().then(function(buffer) {
                fileDownload(buffer, filename);
            });
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal Server Error')
        }
    };
    
    return (
        <div>
            <div>
                {/* Content Header (Page header) */}
                <section className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1>Manage Users</h1>
                            </div>
                            <div className="col-sm-6">
                                <ol className="breadcrumb float-sm-right">
                                    <li className="breadcrumb-item"><a href="/">Home</a></li>
                                    <li className="breadcrumb-item active">Manage Users</li>
                                </ol>
                            </div>
                        </div>
                    </div>{/* /.container-fluid */}
                </section>
                {/* Main content */}
                <section className="content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div style={{ marginBottom: 10 }} className="row">
                                    
                                </div>
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Manage Users</h3>
                                        <div className="col-lg-12 text-right">
                                            { permissions?.users?.create && <Link className="btn btn-primary" to="/users/add" > + Add New User </Link> }
                                        </div>
                                    </div>
                                    {/* /.card-header */}
                                    <div className="card-body">
                                        <div className="row mb-2">
                                            <div className="col-sm-2">
                                                <input type="text" name='firstName' className="form-control" value={search.firstName} onChange={(e) => setSearch({ ...search, firstName: e.target.value })} placeholder="Search First Name" />
                                            </div>
                                            <div className="col-sm-2">
                                                <input type="text" name='lastName' className="form-control" value={search.lastName} onChange={(e) => setSearch({ ...search, lastName: e.target.value })} placeholder="Search Last Name" />
                                            </div>
                                            <div className="col-sm-2">
                                                <input type="text" name='email' className="form-control" value={search.email} onChange={(e) => setSearch({ ...search, email: e.target.value })} placeholder="Search Email" />
                                            </div>
                                            <div className="col-sm-2">
                                                <input type="text" name='phone' className="form-control" value={search.phone} onChange={(e) => setSearch({ ...search, phone: e.target.value })} placeholder="Search Phone" />
                                            </div>
                                            <div className="col-sm-2">
                                                <select className="form-control" name="role" onChange={(e) => setSearch({ ...search, role: e.target.value })} value={search.role}>
                                                    <option value="">Search By Role</option>
                                                    {roles.map((obj) => (
                                                        <option key={obj.slug} value={obj.slug}>{obj.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-sm-2">
                                                <select className="form-control" name="status" onChange={(e) => setSearch({ ...search, status: e.target.value })} value={search.status}>
                                                    <option value="">Search By Status</option>
                                                    {userStatus.map((obj) => (
                                                        <option key={obj.value} value={obj.value}>{obj.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="row mb-2">
                                            <div className="col-sm-3">
                                                <Select
                                                    placeholder="Search By Franchise"
                                                    options={franchises.map((f: any) => ({ value: f.id, label: f.name }))}
                                                    onChange={(opt: any) => {
                                                        const fId = opt ? opt.value.toString() : '';
                                                        setSearch({ ...search, franchiseId: fId, centreId: '' });
                                                        setCentres([]);
                                                        if (opt) fetchCentres(opt.value);
                                                    }}
                                                    value={franchises.map((f: any) => ({ value: f.id, label: f.name })).find((o: any) => o.value.toString() === search.franchiseId) || null}
                                                    isClearable
                                                    isSearchable
                                                />
                                            </div>
                                            <div className="col-sm-2">
                                                <select
                                                    className="form-control"
                                                    value={search.centreId}
                                                    onChange={(e) => setSearch({ ...search, centreId: e.target.value })}
                                                    disabled={!search.franchiseId}
                                                >
                                                    <option value="">Search By Centre</option>
                                                    {centres.map((c: any) => (
                                                        <option key={c.id} value={c.id}>{c.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className='col-sm-3'>
                                                <Button type="submit" isLoading={isLoading} onClick={handleResetFilter} className="btn btn-warning mr-2">Reset Filter</Button>
                                                { permissions?.users?.['export-csv'] &&
                                                    <Button type="submit" isLoading={isLoading} onClick={exportCSV} className="btn btn-success"><i className="fas fa-file-csv"></i> Export</Button>
                                                }
                                            </div>
                                        </div>
                                        <DataTable
                                            columns={columns}
                                            data={users}
                                            pagination
                                            paginationServer
                                            paginationTotalRows={totalRows}
                                            paginationDefaultPage={currentPage}
                                            onChangePage={handlePageChange}
                                            onChangeRowsPerPage={handlePerRowsChange}
                                            paginationPerPage={perPage}
                                            paginationRowsPerPageOptions={[5, 10, 20, 30, 50, 100]}
                                            progressPending={isLoading}
                                            sortServer
                                            onSort={handleSort}
                                            // paginationComponent
                                            // selectableRows
                                            customStyles={customStyle}
                                        />
                                    </div>
                                    {/* /.card-body */}
                                </div>
                            </div>
                            {/* /.col */}
                        </div>
                        {/* /.row */}
                    </div>
                    {/* /.container-fluid */}
                </section>
                {/* /.content */}
            </div>

            <AssignMentorCentresModal
                show={showMentorModal}
                user={mentorUser}
                handleClose={() => { setShowMentorModal(false); setMentorUser(null); }}
                onSuccess={() => { setShowMentorModal(false); setMentorUser(null); getAllUsersData(currentPage, perPage, sortField, sortDirection); }}
            />
        </div>
    )
};

export default ListUsers;
